import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    EmployeeWriteRequest,
    PayrollRunRequest,
} from '@/api/handlers/resources/salaries/requests';
import type {
    CommitPayrollRunResponse,
    CreateEmployeeResponse,
    PreviewPayrollRunResponse,
    UpdateEmployeeResponse,
} from '@/api/handlers/resources/salaries/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import type { JournalEntry } from '@/api/models';
import { AccountQueryKeys } from '../accounts/accountQueryKeys';
import { JournalQueryKeys } from '../journal/journalQueryKeys';
import { SalaryQueryKeys } from './salaryQueryKeys';

/**
 * Create an employee. Idempotent on the client-supplied id. Hiring someone is
 * not a transaction, so no entry is produced and only the register refetches.
 */
export const useCreateEmployeeMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CreateEmployeeResponse>,
        Error,
        EmployeeWriteRequest
    >({
        mutationFn: (body) => apiClient.salaries.createEmployee(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: SalaryQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Edit an employee — including dating their termination, which is what
 * terminating *is*. Touches no ledger account, so only the register refetches.
 */
export const useUpdateEmployeeMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<UpdateEmployeeResponse>,
        Error,
        { employeeId: string; body: EmployeeWriteRequest }
    >({
        mutationFn: ({ employeeId, body }) =>
            apiClient.salaries.updateEmployee(employeeId, body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: SalaryQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Remove an employee no run has ever charged.
 *
 * Refused with `EMPLOYEE_HAS_RUNS` for anyone who has been, and the caller shows
 * that rather than retrying — there is no force flag, and dating a departure is
 * the operation that ends an employment without disowning its history.
 *
 * Only the register refetches: a deletable employee is by definition one no
 * entry was ever built from, so no ledger balance can have moved.
 */
export const useDeleteEmployeeMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<void>, Error, string>({
        mutationFn: (employeeId) =>
            apiClient.salaries.deleteEmployee(employeeId),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: SalaryQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Preview a period's payroll: per-employee amounts, the totals, and the draft a
 * commit would build. **Pure** — it writes nothing, so it invalidates nothing.
 */
export const usePreviewPayrollRunMutation = () => {
    const { apiClient } = useApi();

    return useMutation<
        ApiResponse<PreviewPayrollRunResponse>,
        Error,
        PayrollRunRequest
    >({
        mutationFn: (body) => apiClient.salaries.previewPayrollRun(body),
    });
};

/**
 * Create the period's DRAFT entry and record the run.
 *
 * **Nothing is posted**, so nothing in the ledger has moved: the accounts are
 * deliberately *not* invalidated here. What has changed is the register — the
 * run list gains a row, and re-previewing the period now reports an
 * `existingRun` — so `['salaries']` refetches, and the journal gains a draft.
 *
 * The account balances move later, when someone posts that draft. That is what
 * {@link useInvalidatePostedPayrollEntry} is for.
 */
export const useCommitPayrollRunMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<CommitPayrollRunResponse>,
        Error,
        PayrollRunRequest
    >({
        mutationFn: (body) => apiClient.salaries.commitPayrollRun(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: SalaryQueryKeys.all,
                });
                queryClient.invalidateQueries({
                    queryKey: JournalQueryKeys.all,
                });
            }
        },
    });
};

/**
 * The run's entry was **posted** — tell the three feeds that just moved.
 *
 * Posting is the only event that changes what payroll has accrued: the register
 * derives every accrued figure from run lines whose entry has actually posted,
 * and the ledger side of the comparison is the account balances. The journal's
 * own post mutation invalidates only the journal, so without this the summary
 * and the comparison keep showing their pre-post state — a committed run reading
 * as money owed when nothing has been charged, or the reverse.
 *
 * A save that did not post moves nothing: the draft already existed.
 */
export const useInvalidatePostedPayrollEntry = () => {
    const queryClient = useQueryClient();

    return React.useCallback(
        (entry: Pick<JournalEntry, 'status'> | null | undefined) => {
            if (entry?.status !== 'POSTED') return;
            queryClient.invalidateQueries({ queryKey: SalaryQueryKeys.all });
            queryClient.invalidateQueries({ queryKey: JournalQueryKeys.all });
            // The balances the accrued-vs-ledger comparison reads.
            queryClient.invalidateQueries({ queryKey: AccountQueryKeys.all });
        },
        [queryClient],
    );
};
